import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { SearchField } from "@/components/app/patterns/search-field";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { THERAPY_DISCIPLINES, errorWords } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { NewRowBuildingSelect } from "@/components/app/fields/new-row-building-select";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCan, useCanChangeRow } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { CptCodeForm, type CptCodeValues } from "./-components/care/cpt-code-form";
import { CptImportDialog } from "./-components/care/cpt-import-dialog";
import { NoMatches } from "@/routes/_authenticated/_administration/settings/-components/no-matches";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";

export const Route = createFileRoute("/_authenticated/_administration/settings/therapy-cpt-codes")({
  validateSearch: (search: Record<string, unknown>): { q?: string; retired?: true } => ({
    q: optionalSearch(search.q),
    retired: search.retired === true ? true : undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "therapy-cpt-codes"),
  component: TherapyCptCodesSettings,
});

type CptCode = Output<typeof orpcClient.care.therapy.cptCodes>[number];

const ANY = "all";
const fail = (error: Error) => toast.error(errorWords(error));
const disciplineLabel = (value: string) =>
  THERAPY_DISCIPLINES.find((row) => row.value === value)?.label ?? value;

/** The CPT codes a therapist can bill under, and which discipline each belongs to. */
function TherapyCptCodesSettings() {
  const { facilityId } = useFacility();
  const canManage = useCan("therapy:manage");
  // A code every building bills is the organization's therapy managers' to add or change; a
  // building's therapy manager keeps codes for their own building.
  const canChange = useCanChangeRow("therapy:manage");
  const place = useNewRowPlace("therapy:manage");
  const [chosenAt, setChosenAt] = useState<string | null>();
  const placedAt = chosenAt === undefined ? (place.everyBuilding ? null : place.startAt) : chosenAt;
  const canAdd = place.everyBuilding || place.buildings.length > 0;
  const building = (
    <NewRowBuildingSelect id="cpt-building" place={place} value={placedAt} onChange={setChosenAt} />
  );
  const queryClient = useQueryClient();
  const [discipline, setDiscipline] = useState(ANY);
  const { q, retired: showRetired } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));
  const setShowRetired = (next: boolean) => patch({ retired: next ? true : undefined });
  const [editing, setEditing] = useState<CptCode | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [retiring, setRetiring] = useState<CptCode | null>(null);

  const codes = useQuery(
    orpc.care.therapy.cptCodes.queryOptions({
      input: {
        facilityId,
        discipline: discipline === ANY ? undefined : discipline,
        includeInactive: showRetired,
      },
    }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.care.therapy.cptCodes.key() });

  const save = useMutation(
    orpc.care.therapy.saveCptCode.mutationOptions({
      onSuccess: async () => {
        toast.success("CPT code saved");
        setAdding(false);
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const deactivate = useMutation(
    orpc.care.therapy.deactivateCptCode.mutationOptions({
      onSuccess: async () => {
        toast.success("CPT code retired");
        setRetiring(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const importCodes = useMutation(
    orpc.care.therapy.importCptCodes.mutationOptions({
      onSuccess: async ({ imported, skipped }) => {
        // The skipped count is the whole answer to "why are there fewer codes
        // than lines I pasted", so it is said even when it is zero.
        toast.success(`Imported ${imported} code(s), ${skipped} already in the catalog`);
        setImporting(false);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const submit = (values: CptCodeValues) =>
    save.mutate({ ...values, facilityId: values.cptCodeId ? undefined : placedAt });

  const needle = search.trim().toLowerCase();
  const rows = (codes.data ?? [])
    .filter(
      (row) =>
        !needle ||
        row.code.toLowerCase().includes(needle) ||
        (row.description ?? "").toLowerCase().includes(needle),
    )
    .toSorted((a, b) => a.code.localeCompare(b.code));

  return (
    <>
      <PageHeader
        title="Interventions"
        meta="The CPT codes therapists bill under on a therapy note, and the discipline each belongs to."
        actions={
          canManage && canAdd ? (
            <Button onClick={() => setImporting(true)} variant="outline">
              Import CSV
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchField value={search} onChange={setSearch} placeholder="Search codes" />
        <Select value={discipline} onValueChange={setDiscipline}>
          <SelectTrigger aria-label="Discipline" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Every discipline</SelectItem>
            {THERAPY_DISCIPLINES.map((row) => (
              <SelectItem key={row.value} value={row.value}>
                {row.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch checked={showRetired} id="cpt-show-retired" onCheckedChange={setShowRetired} />
          <Label htmlFor="cpt-show-retired">Show retired codes</Label>
        </div>
      </div>

      <RecordCard
        title="Codes"
        count={codes.data ? rows.length : undefined}
        canWrite={canManage && canAdd}
        addLabel="Add code"
        onAdd={() => {
          setEditing(null);
          setAdding(true);
        }}
      >
        {codes.isPending ? (
          <RowsSkeleton />
        ) : codes.isError ? (
          <ReadFailed
            message="The codes could not be loaded."
            error={codes.error}
            retrying={codes.isFetching}
            onRetry={() => void codes.refetch()}
          />
        ) : rows.length === 0 ? (
          needle ? (
            <NoMatches search={search} onClear={() => setSearch("")} />
          ) : discipline !== ANY ? (
            <EmptyRows text={`No ${disciplineLabel(discipline)} codes yet.`} />
          ) : (
            <EmptyRows text="No CPT codes yet. Add one, or import your list, so therapists can pick it on a therapy note." />
          )
        ) : (
          <ul className="divide-y">
            {rows.map((code) => (
              <RecordRow
                key={code.id}
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono">{code.code}</span>
                    <Badge variant="outline">{disciplineLabel(code.discipline)}</Badge>
                    {code.isActive ? null : <Badge variant="secondary">Retired</Badge>}
                  </span>
                }
                meta={code.description}
                actions={
                  canManage && code.organizationId !== null && canChange(code.facilityId) ? (
                    <RowActions
                      modal={false}
                      label={code.code}
                      primary={{ label: "Edit", onSelect: () => setEditing(code) }}
                      items={[
                        code.isActive && {
                          label: "Retire code",
                          destructive: true,
                          onSelect: () => setRetiring(code),
                        },
                      ]}
                    />
                  ) : canManage && code.organizationId === null ? (
                    <SharedRowNote reason="A standard code every operator starts with, so it cannot be changed here." />
                  ) : canManage && !code.facilityId ? (
                    <SharedRowNote />
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <Dialog
        open={adding || editing !== null}
        onOpenChange={(open) => {
          if (open) return;
          setAdding(false);
          setEditing(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit code" : "Add code"}</DialogTitle>
          </DialogHeader>
          {editing ? null : building}
          <CptCodeForm
            key={editing?.id ?? "new"}
            defaultValues={
              editing
                ? {
                    cptCodeId: editing.id,
                    code: editing.code,
                    description: editing.description,
                    discipline: editing.discipline,
                    isActive: editing.isActive,
                  }
                : undefined
            }
            pending={save.isPending}
            submitLabel={editing ? "Save code" : "Add code"}
            onSubmit={submit}
            onCancel={() => {
              setAdding(false);
              setEditing(null);
            }}
          />
        </DialogContent>
      </Dialog>

      <CptImportDialog
        onImport={(pasted) => importCodes.mutate({ facilityId: placedAt, rows: pasted })}
        building={building}
        onOpenChange={setImporting}
        open={importing}
        pending={importCodes.isPending}
      />

      <AlertDialog
        open={retiring !== null}
        onOpenChange={(open) => (open ? null : setRetiring(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire {retiring?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops being offered on new therapy notes. Charges already saved against it keep the
              code they quoted, and it can be switched back on from Edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => retiring && deactivate.mutate({ cptCodeId: retiring.id })}
            >
              Retire code
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
